import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IMAGES } from '../constants/assets';
import SectionWrapper from './SectionWrapper';
import { useAuth } from '../context/AuthContextOIDC';
import { useNotification } from '../context/NotificationContext';
import { useThemeMode } from '../context/ThemeContext';
import { useUserRole } from '../hooks/useUserRole';
import { userManagementService, dashboardService } from '../services';
import { refreshAll } from '../services/refreshService';
import { DashboardStatus } from '../types';
import { useGlobalPolling, POLLING_CONFIG } from '../config/polling';
import LanguageSelector, { LanguageMenuItem } from './common/LanguageSelector';

interface Page {
  name: string;
  nameKey: string;
  icon: React.ReactElement;
  path: string;
}

const getPages = (t: (key: string) => string, userRole?: string, isDark?: boolean): Page[] => {
  // Icons werden ohne inline-Filter gerendert, Filter wird über CSS im Button gesteuert
  const basePage = {
    name: t('navbar:pages.overview'),
    nameKey: 'overview',
    icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt="Overview" className="nav-icon" />,
    path: '/dienstleister'
  };

  // 🔐 QS und SuperQS sehen QS-Bereich
  if (userRole === 'qs' || userRole === 'super_qs') {
    return [
      basePage,
      { name: t('navbar:pages.qsAnalyses'), nameKey: 'qsAnalyses', icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt="QS" className="nav-icon" />, path: '/qs' }
    ];
  }

  // 🔐 Nur Admins sehen den Admin-Bereich
  if (userRole === 'admin') {
    return [
      basePage,
      { name: t('navbar:pages.qsAnalyses'), nameKey: 'qsAnalyses', icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt="QS" className="nav-icon" />, path: '/qs' },
      { name: t('navbar:pages.admin'), nameKey: 'admin', icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt="Admin" className="nav-icon" />, path: '/admin' }
    ];
  }

  // 📖 Reader sehen nur Übersicht
  return [basePage];
};

const ResponsiveAppBar: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { showNotification } = useNotification();
  const { mode, toggleTheme, isDark } = useThemeMode();
  const { role: userRole, user: userWithOrg, isLoading: roleLoading, error: roleError, isAdminInForeignOrganization } = useUserRole();

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [anchorElLanguage, setAnchorElLanguage] = React.useState<null | HTMLElement>(null);
  const [dashboardData, setDashboardData] = React.useState<DashboardStatus | null>(null);

  // 🔐 Dynamische Pages basierend auf echter Azure AD Rolle
  const pages = getPages(t, userRole, isDark);

  // 📊 Credit-Information laden mit globalem Polling
  const fetchDashboardData = React.useCallback(async () => {
    if (isAuthenticated) {
      try {
        const data = await dashboardService.getDashboardStatus();
        setDashboardData(data);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      }
    }
  }, [isAuthenticated]);

  // Initial laden
  React.useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // An globales Polling anküpfen
  useGlobalPolling('navbar-credits', fetchDashboardData);

  // Credit-Werte direkt vom Backend verwenden
  const remainingCredits = dashboardData?.remainingCredits ?? null;
  const pendingCredits = dashboardData?.pendingCredits ?? 0;
  const isUnlimited = dashboardData?.creditsUnlimited ?? (dashboardData?.analysisCredits === null || dashboardData?.analysisCredits === undefined);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElLanguage(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseLanguageMenu = () => {
    setAnchorElLanguage(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path: string): boolean => {
    if (path === '/dienstleister') {
      // return location.pathname.startsWith('/dienstleister');
    }
    if (path === '/admin') {
      return location.pathname.startsWith('/admin');
    }
    return location.pathname === path;
  };

  // Generiere Initialen aus dem Benutzernamen
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    refreshAll();
    handleCloseNavMenu();
  };

  return (
    <AppBar
      position="static"
      sx={{
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        height: '60px'
      }}
    >
      <SectionWrapper>
        <Container>
          <Toolbar disableGutters sx={{ minHeight: '60px', height: '60px' }}>
            <Typography
              variant="h6"
              noWrap
              onClick={() => navigate('/')}
              data-testid="navbar-logo-link"
              sx={{
                display: { xs: 'none', md: 'flex' },
                marginRight: '50px',
              }}
            >
              <img
                src={isDark ? IMAGES.LOGO_DARK : IMAGES.LOGO}
                alt="DORAsmartCheck.ai"
                style={{
                  height: '26px'
                }}
              />
            </Typography>



            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
                data-testid="navbar-mobile-menu-button"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{ display: { xs: 'block', md: 'none' } }}
              >
                {pages.map((page) => (
                  <MenuItem
                    key={page.nameKey}
                    onClick={() => handleNavigate(page.path)}
                    selected={isActive(page.path)}
                    data-testid={`navbar-mobile-${page.nameKey.toLowerCase()}-link`}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: isActive(page.path) ? 'text.primary' : 'text.secondary'
                    }}>
                      {page.icon}
                      <Typography sx={{
                        textAlign: 'center',
                        color: 'inherit',
                        fontWeight: isActive(page.path) ? 700 : 500,
                      }}>
                        {page.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            <Typography
              variant="h6"
              noWrap
              onClick={() => navigate('/')}
              sx={{
                alignItems: 'center',
                display: { xs: 'flex', md: 'none' },
              }}
            >
              <img
                src={isDark ? IMAGES.LOGO_DARK : IMAGES.LOGO}
                alt="DORAsmartCheck.ai"
                style={{
                  height: '26px'
                }}
              />
            </Typography>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex', gap: 50 } }}>
              {pages.map((page) => (
                <Button
                  key={page.nameKey}
                  onClick={() => handleNavigate(page.path)}
                  data-testid={`navbar-${page.nameKey.toLowerCase()}-link`}
                  sx={{
                    my: 1,
                    color: "text.primary",
                    display: 'block',
                    fontWeight: 500,
                    boxSizing: 'border-box',
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    gap: 2,
                    position: 'relative',
                    // Icon-Filter für Dark Mode
                    '& .nav-icon': {
                      filter: isDark ? 'brightness(0) invert(1)' : 'none',
                      transition: 'filter 0.2s ease-in-out',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      bottom: '-9px',
                      width: '100%',
                      height: '2px',
                      backgroundColor: "primary.yellow",
                      transform: isActive(page.path) ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease-in-out 0s',
                    },
                    '&:hover::after': {
                      transform: 'scaleX(1)',
                      transition: 'transform 0.3s ease-in-out 0s',
                    },
                    '&:not(:hover)::after': {
                      transform: isActive(page.path) ? 'scaleX(1)' : 'scaleX(0)',
                      transition: 'none',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.125rem' }}>
                    {page.icon}
                    {page.name}
                  </Box>
                </Button>
              ))}
            </Box>

            {/* Admin Foreign Organization Warning - kompakt neben Profil */}
            {isAdminInForeignOrganization && userWithOrg?.organization?.name && (
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                bgcolor: '#fef2f2 !important',
                border: '2px solid #dc2626',
                borderRadius: '8px',
                px: 2,
                py: 1,
                mr: 2,
                minWidth: 'max-content',
                // Verhindere Dark Mode Override
                '& *': {
                  color: '#dc2626 !important'
                }
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#dc2626 !important'
                }} />
                <Typography sx={{
                  fontSize: '0.8rem',
                  color: '#dc2626 !important',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}>
                  {userWithOrg.organization.name}
                </Typography>
                <Button
                  size="small"
                  data-testid="navbar-admin-return-super-org-button"
                  onClick={async () => {
                    try {
                      // 1. Bestätigung vom Benutzer
                      const currentOrgName = userWithOrg?.organization?.name || 'unknown organization';
                      const confirmMessage = t('navbar:adminWarning.confirmReturn', { orgName: currentOrgName });

                      if (!window.confirm(confirmMessage)) {
                        return;
                      }

                      // 2. Backend-Endpunkt aufrufen (findet automatisch die Super-Organisation)
                      const result = await userManagementService.returnToSuperOrganization();

                      if (result.status === 'success') {
                        // Backend Response hat die Nachricht in result.data.message
                        const message = result.data?.message || t('navbar:adminWarning.returnSuccess');
                        showNotification(message, 'success');

                        // 3. Seite neu laden um die Änderungen zu reflektieren
                        window.location.reload();
                      } else {
                        showNotification(t('navbar:adminWarning.unexpectedError'), 'error');
                      }

                    } catch (error) {
                      console.error('Error returning to super organization:', error);
                      showNotification(t('navbar:adminWarning.returnError'), 'error');
                    }
                  }}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.25,
                    fontSize: '0.7rem',
                    textTransform: 'none',
                    bgcolor: '#dc2626 !important',
                    color: 'white !important',
                    ml: 1,
                    '&:hover': {
                      bgcolor: '#b91c1c !important'
                    }
                  }}
                >
                  {t('navbar:adminWarning.returnButton')}
                </Button>
              </Box>
            )}

            {/* 📊 Credit-Anzeige */}
            {!isUnlimited && (
              <Tooltip title={t('navbar:credits.tooltip', {
                used: dashboardData?.usedCredits ?? 0,
                total: dashboardData?.analysisCredits ?? 0
              })}>
                <Chip
                  size="small"
                  label={
                    pendingCredits > 0
                      ? t('navbar:credits.displayWithPending', { remaining: remainingCredits, pending: pendingCredits })
                      : t('navbar:credits.display', { remaining: remainingCredits })
                  }
                  sx={{
                    mr: 2,
                    bgcolor: remainingCredits && remainingCredits > 0 ? '#dcfce7' : '#fef2f2',
                    color: remainingCredits && remainingCredits > 0 ? '#166534' : '#dc2626',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    border: remainingCredits && remainingCredits > 0 ? '1px solid #86efac' : '1px solid #fecaca',
                    display: { xs: 'none', md: 'flex' }
                  }}
                  data-testid="navbar-credits-display"
                />
              </Tooltip>
            )}

            {/* 🌙 Theme Toggle Button */}
            <Tooltip title={isDark ? t('navbar:theme.toggleLight') : t('navbar:theme.toggleDark')}>
              <IconButton
                onClick={toggleTheme}
                data-testid="navbar-theme-toggle"
                sx={{
                  mr: 1,
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                {isDark ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title={t('navbar:userMenu.openMenu')}>
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} data-testid="navbar-user-menu-button">
                  <Avatar
                    sx={{
                      bgcolor: 'primary.blue',
                      color: 'white',
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem'
                    }}
                  >
                    {user ? getInitials(user.name) : 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {user?.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {userWithOrg?.organization?.name || t('common:labels.noOrganization')}
                    </Typography>
                  </Box>
                </MenuItem>

                {/* Language Selector */}
                <LanguageMenuItem onClick={handleOpenLanguageMenu} />

                <MenuItem onClick={() => { navigate('/settings/api-keys'); handleCloseUserMenu(); }} data-testid="navbar-user-api-keys-link">
                  <Typography sx={{
                    textAlign: 'center',
                    color: 'text.primary'
                  }}>
                    {t('navbar:userMenu.apiKeys')}
                  </Typography>
                </MenuItem>

                {/* Divider */}
                <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1 }} />

                {/* Legal Links */}
                <MenuItem
                  onClick={() => {
                    window.open('https://www.dorasmartcheck.ai/impressum', '_blank', 'noopener,noreferrer');
                    handleCloseUserMenu();
                  }}
                  data-testid="navbar-user-impressum-link"
                >
                  <Typography sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    {t('navbar:userMenu.imprint')}
                  </Typography>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    window.open('https://www.dorasmartcheck.ai/datenschutz', '_blank', 'noopener,noreferrer');
                    handleCloseUserMenu();
                  }}
                  data-testid="navbar-user-datenschutz-link"
                >
                  <Typography sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    {t('navbar:userMenu.privacy')}
                  </Typography>
                </MenuItem>

                {/* Divider */}
                <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1 }} />

                {isAuthenticated && (
                  <MenuItem onClick={handleLogout} data-testid="navbar-user-logout-button">
                    <Typography sx={{
                      textAlign: 'center',
                      color: 'error.main'
                    }}>
                      {t('navbar:userMenu.logout')}
                    </Typography>
                  </MenuItem>
                )}
              </Menu>

              {/* Language Submenu */}
              <LanguageSelector
                anchorEl={anchorElLanguage}
                open={Boolean(anchorElLanguage)}
                onClose={handleCloseLanguageMenu}
              />
            </Box>
          </Toolbar>
        </Container>
      </SectionWrapper>
    </AppBar >
  );
}

export default ResponsiveAppBar;
